import React, { useEffect, useState } from 'react';
import ModalBody from '../../ModalBody';
import CustomTextInput from '../../../CustomTextInput';
import { useTranslation } from 'react-i18next';
import { crudStatus } from '../../../../utils/enums';
import ToggleLine from '../../../ToggleLine';
import { useCreateCompanyMutation, useUpdateCompanyMutation } from '../../../../hooks/mutations/useCompanyMutation';
import { Company } from '../../../../services/apiSLTP/materials/companies';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getCoordsFromAdresseDataGouv } from '../../../../utils/numbers';

interface ModalProps {
    company: Company | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateCompanyModal: React.FC<ModalProps> = ({ company, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(company?.designation || '');
    const [codeProC, setCodeProC] = useState(company?.codeProC || '');
    const [email, setEmail] = useState(company?.email || '');
    const [phone, setPhone] = useState(company?.phone || '');
    const [address, setAddress] = useState(company?.address || '');
    const [latitude, setLatitude] = useState(company?.latitude?.toString() || '');
    const [longitude, setLongitude] = useState(company?.longitude?.toString() || '');
    const [status, setStatus] = useState(company?.crudStatus || crudStatus.OK);
    const [isGnrSupplier, setIsGnrSupplier] = useState(company?.isGnrSupplier || false);
    const [isSubcontractor, setIsSubcontractor] = useState(company?.isSubcontractor || false);
    const [isToolSupplier, setIsToolSupplier] = useState(company?.isToolSupplier || false);
    const [isTransportCompany, setIsTransportCompany] = useState(company?.isTransportCompany || false);
    const [isSiteSupplier, setIsSiteSupplier] = useState(company?.isSiteSupplier || false);

    const { mutate: updateCompany } = useUpdateCompanyMutation({
        onSuccess: () => refetch(),
    });

    const { mutate: createCompany } = useCreateCompanyMutation({
        onSuccess: () => refetch(),
    });

    const handleOk = () => {
        const payload = {
            designation,
            codeProC,
            email,
            crudStatus: status,
            isGnrSupplier,
            isSubcontractor,
            isToolSupplier,
            isTransportCompany,
            isSiteSupplier,
            phone: phone || undefined,
            latitude: latitude ? parseFloat(latitude) : null,
            longitude: longitude ? parseFloat(longitude) : null,
            address,
        };

        isEditMode ? updateCompany({ companyId: company?._id || '', data: payload }) : createCompany(payload);
    };

    useEffect(() => {
        const computePoint = async () => {
            if (!address.length || (latitude && longitude)) return;
            const point = await getCoordsFromAdresseDataGouv(address);
            if (!latitude) setLatitude(point?.lat.toString() || '');
            if (!longitude) setLongitude(point?.lon.toString() || '');
        };

        computePoint();
    }, [address, latitude, longitude]);

    return (
        <ModalBody
            title={t('company')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disabledOk={!designation || !codeProC}
            handleOk={handleOk}
        >
            <CustomTextInput value={designation} label={t('designation')} onChange={setDesignation} required />
            <CustomTextInput value={codeProC} label={t('codeProC')} onChange={setCodeProC} required />
            <ToggleLine
                checked={status === crudStatus.OK}
                onChange={(active) => setStatus(active ? crudStatus.OK : crudStatus.INACTIVE)}
                title={capitalizeFirstLetter(t('active'))}
            />
            <CustomTextInput value={address} label={t('address')} onChange={setAddress} />
            <CustomTextInput value={latitude} label={t('latitude')} onChange={(text) => setLatitude(text)} type="number" />
            <CustomTextInput value={longitude} label={t('longitude')} onChange={(text) => setLongitude(text)} type="number" />
            <CustomTextInput value={email} label={t('email')} onChange={setEmail} />
            <CustomTextInput value={phone} label={t('phone')} onChange={setPhone} />
            {/*<ToggleLine checked={isGnrSupplier} onChange={setIsGnrSupplier} title="GNR Supplier" />
            <ToggleLine checked={isSubcontractor} onChange={setIsSubcontractor} title="Subcontractor" />
            <ToggleLine checked={isToolSupplier} onChange={setIsToolSupplier} title="Tool Supplier" />
            <ToggleLine checked={isTransportCompany} onChange={setIsTransportCompany} title="Transport Company" />
            <ToggleLine checked={isSiteSupplier} onChange={setIsSiteSupplier} title="Site Supplies" />*/}
        </ModalBody>
    );
};

export default UpdateCompanyModal;
